import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE = `${process.env.REACT_APP_API_URL}/api/addresses`;

const getHeaders = (thunkAPI) => {
  const token =
    thunkAPI.getState().auth.token || localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
};

/* ── Thunks ──────────────────────────────────────────── */

export const fetchAddresses = createAsyncThunk(
  "address/fetchAll",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(BASE, { headers: getHeaders(thunkAPI) });
      return res.data.addresses;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to fetch addresses");
    }
  }
);

export const saveAddress = createAsyncThunk(
  "address/save",
  async (addressData, thunkAPI) => {
    try {
      const res = await axios.post(BASE, addressData, { headers: getHeaders(thunkAPI) });
      return res.data.address;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to save address");
    }
  }
);

export const updateAddress = createAsyncThunk(
  "address/update",
  async ({ id, ...addressData }, thunkAPI) => {
    try {
      const res = await axios.put(`${BASE}/${id}`, addressData, { headers: getHeaders(thunkAPI) });
      return res.data.address;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to update address");
    }
  }
);

export const deleteAddress = createAsyncThunk(
  "address/delete",
  async (id, thunkAPI) => {
    try {
      await axios.delete(`${BASE}/${id}`, { headers: getHeaders(thunkAPI) });
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to delete address");
    }
  }
);

export const setDefaultAddress = createAsyncThunk(
  "address/setDefault",
  async (id, thunkAPI) => {
    try {
      const res = await axios.patch(`${BASE}/${id}/set-default`, {}, { headers: getHeaders(thunkAPI) });
      return res.data.address;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to set default");
    }
  }
);

/* ── Slice ───────────────────────────────────────────── */

const addressSlice = createSlice({
  name: "address",
  initialState: {
    addresses:       [],
    selectedAddress: null,   // address chosen at checkout
    loading:         false,
    error:           null,
  },
  reducers: {
    selectAddress(state, action) {
      state.selectedAddress = action.payload;   // pass full address object
    },
    clearSelectedAddress(state) {
      state.selectedAddress = null;
    },
  },
  extraReducers: (builder) => {
    const pending  = (state)         => { state.loading = true;  state.error = null; };
    const rejected = (state, action) => { state.loading = false; state.error = action.payload; };

    builder
      /* fetch */
      .addCase(fetchAddresses.pending,   pending)
      .addCase(fetchAddresses.rejected,  rejected)
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.loading   = false;
        state.addresses = action.payload;
        // auto-select default on load
        if (!state.selectedAddress) {
          state.selectedAddress = action.payload.find((a) => a.isDefault) || action.payload[0] || null;
        }
      })

      /* save */
      .addCase(saveAddress.pending,   pending)
      .addCase(saveAddress.rejected,  rejected)
      .addCase(saveAddress.fulfilled, (state, action) => {
        state.loading = false;
        state.addresses.unshift(action.payload);
        if (action.payload.isDefault) {
          state.addresses = state.addresses.map((a) =>
            a._id === action.payload._id ? a : { ...a, isDefault: false }
          );
          state.selectedAddress = action.payload;
        }
      })

      /* update */
      .addCase(updateAddress.pending,   pending)
      .addCase(updateAddress.rejected,  rejected)
      .addCase(updateAddress.fulfilled, (state, action) => {
        state.loading   = false;
        state.addresses = state.addresses.map((a) =>
          a._id === action.payload._id ? action.payload : a
        );
        if (state.selectedAddress?._id === action.payload._id) {
          state.selectedAddress = action.payload;
        }
      })

      /* delete */
      .addCase(deleteAddress.pending,   pending)
      .addCase(deleteAddress.rejected,  rejected)
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.loading   = false;
        state.addresses = state.addresses.filter((a) => a._id !== action.payload);
        if (state.selectedAddress?._id === action.payload) {
          state.selectedAddress = state.addresses[0] || null;
        }
      })

      /* set default */
      .addCase(setDefaultAddress.pending,   pending)
      .addCase(setDefaultAddress.rejected,  rejected)
      .addCase(setDefaultAddress.fulfilled, (state, action) => {
        state.loading   = false;
        state.addresses = state.addresses.map((a) =>
          a._id === action.payload._id
            ? action.payload
            : { ...a, isDefault: false }
        );
        state.selectedAddress = action.payload;
      });
  },
});

export const { selectAddress, clearSelectedAddress } = addressSlice.actions;
export default addressSlice.reducer;